import {Inject, Injectable, PLATFORM_ID} from '@angular/core'
import {DOCUMENT, isPlatformBrowser} from '@angular/common'

@Injectable({
  providedIn: 'root',
})
export class DocumentService {
  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(PLATFORM_ID) private platformId: Record<string, unknown>,
  ) {}

  disablePageScroll(): void {
    this.document.body.style.overflow = 'hidden'
  }

  enablePageScroll(): void {
    this.document.body.style.overflow = 'auto'
  }

  scrollToTop(behavior: ScrollBehavior = 'smooth'): void {
    this.scrollToPositionY(0, behavior)
  }

  scrollToBottom(behavior: ScrollBehavior = 'smooth'): void {
    this.scrollToPositionY(this.document.body.scrollHeight, behavior)
  }

  scrollToElementY(element: Element, options: ScrollOptions = {behaviour: 'smooth', speed: ScrollSpeed.Medium}): void {
    if (!isPlatformBrowser(this.platformId)) {
      return
    }

    if (this.isNativeSmoothScrollSupported() && !options.offset) {
      element.scrollIntoView({
        behavior: options.behaviour ?? 'smooth',
        block: options.block ?? 'start',
      })
      return
    }

    const window = this.document.defaultView
    if (!window) throw Error('Window is not defined')

    const {behaviour, speed} = options
    const clientRect = element.getBoundingClientRect()

    this.scrollToPositionY(window.scrollY + clientRect.top + (options.offset ?? 0), behaviour, speed)
  }

  appendChildToBody(element: HTMLElement): void {
    if (isPlatformBrowser(this.platformId)) {
      this.document.body.appendChild(element)
    }
  }

  smoothScroll(scrollTo: number, options: SmoothScrollOptions): void {
    let currentScrollPosition = options.currentScrollPosition

    const scrollSpeedSetting: ScrollSpeedSetting = options.speed
      ? scrollSpeedSettings[options.speed]
      : scrollSpeedSettings[ScrollSpeed.Medium]
    const {incrementInPx, intervalInMs} = scrollSpeedSetting

    const increment = currentScrollPosition < scrollTo

    const intervalRef = setInterval(() => {
      if ((increment && currentScrollPosition > scrollTo) || (!increment && currentScrollPosition <= scrollTo)) {
        clearInterval(intervalRef)
        currentScrollPosition = scrollTo
      }

      options.scroll(currentScrollPosition)
      currentScrollPosition = increment ? currentScrollPosition + incrementInPx : currentScrollPosition - incrementInPx
    }, intervalInMs)
  }

  private scrollToPositionY(
    y: number,
    behavior: ScrollBehavior = 'smooth',
    speed: ScrollSpeed = ScrollSpeed.Medium,
  ): void {
    if (!isPlatformBrowser(this.platformId)) {
      return
    }

    const window = this.document.defaultView
    if (!window) throw Error('Window is not defined')

    if (behavior === 'smooth') {
      this.smoothScroll(y ?? 0, {
        currentScrollPosition: window.scrollY,
        speed,
        scroll(scrollTo: number) {
          window.scroll({top: scrollTo})
        },
      })

      return
    }

    window.scroll({
      top: y ?? 0,
      left: 0,
      behavior: 'auto',
    })
  }

  private isNativeSmoothScrollSupported(): boolean {
    return 'scrollBehavior' in document.documentElement.style
  }
}

export type ScrollOptions = {
  behaviour?: ScrollBehavior
  speed?: ScrollSpeed
  block?: ScrollLogicalPosition
  offset?: number
}

export enum ScrollSpeed {
  Slow = 'slow',
  Medium = 'medium',
  Fast = 'fast',
}

type SmoothScrollOptions = {
  scroll(scrollTo: number): void
  currentScrollPosition: number
  speed?: ScrollSpeed
}

type ScrollSpeedSetting = {
  incrementInPx: number
  intervalInMs: number
}

const scrollSpeedSettings: {
  [key in ScrollSpeed]: ScrollSpeedSetting
} = {
  [ScrollSpeed.Slow]: {
    incrementInPx: 20,
    intervalInMs: 30,
  },
  [ScrollSpeed.Medium]: {
    incrementInPx: 30,
    intervalInMs: 20,
  },
  [ScrollSpeed.Fast]: {
    incrementInPx: 40,
    intervalInMs: 10,
  },
}
